/* eslint-disable no-alert -- Temporary: Alerts used for user feedback */
/* eslint-disable @typescript-eslint/strict-boolean-expressions -- Temporary: Null checks needed for file operations */
import { useRef, useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectSixDetailMeta, selectCurrentPath } from '@/store/selectors/sixDetail';
import { addFileToHelia } from '@/store/thunks/poc/helia';
import { createFolder } from '@/store/thunks/sixDetail/createFolderThunk';
import type { FileMetadata } from '@/types/poc/helia';
import { createLogger, type Logger } from '@/utils/logger';
import { normalizeFolderPath } from '@/utils/folderPath.utils';

import { normalizeFileMetadata } from './useSixDetail';

const log: Logger = createLogger({ namespace: 'six3ro:hooks:file-upload' });

/**
 * Hook for handling file uploads in a Six
 * Manages file selection, upload state, and interactions with Helia/IPFS
 *
 * @param sixId - ID of the Six to upload files to
 * @returns {Object} Hook state and handlers
 * @returns {boolean} return.isUploading - Whether a file upload is in progress
 * @returns {React.RefObject} return.fileInputRef - Ref for the hidden file input element
 * @returns {Function} return.handleUploadFile - Trigger file selection dialog
 * @returns {Function} return.handleFileSelect - Process selected files
 * @returns {Function} return.handleNewFolder - Handle folder creation
 *
 * @example
 * const { isUploading, fileInputRef, handleUploadFile, handleFileSelect, handleNewFolder } = useSixFileUpload(sixId);
 *
 * return (
 *   <>
 *     <input ref={fileInputRef} type="file" onChange={handleFileSelect} style={{ display: 'none' }} />
 *     <Button onClick={handleUploadFile} disabled={isUploading}>Upload</Button>
 *   </>
 * );
 */
export function useSixFileUpload(sixId: string) {
  const dispatch = useAppDispatch();
  const orgMeta = useAppSelector(selectSixDetailMeta);
  const currentPath = useAppSelector(selectCurrentPath);

  const uploadProgress = useAppSelector(state => state.helia.uploadProgress);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if any files are currently uploading (not complete or error)
  const isUploading = useMemo(
    () => {

      const values = Object.values(uploadProgress);
      return values.some(
        progress => progress.stage !== 'complete' && progress.stage !== 'error'
      );
    },
    [uploadProgress]
  );

  const uploadFile = useCallback(async(file: File): Promise<void> => {
    try {
      // Create FileMetadata from the selected file
      const fileMetadata: FileMetadata = normalizeFileMetadata({
        cid: '', // This will be generated after upload to IPFS/Helia
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        path: currentPath, // Upload to current folder
        decryptionCIDS: [],
      });

      log.info('Starting the upload workflow:', fileMetadata);

      if (!orgMeta) {
        throw new Error('Organization metadata not available');
      }

      const allParticipantIds = [
        ...orgMeta.owners.map(owner => owner.id),
        ...orgMeta.internalParticipants.map(participant => participant.id),
        ...orgMeta.externalParticipants.map(participant => participant.id)
      ];

      await dispatch(addFileToHelia({ sixId, file, allParticipantIds, path: currentPath }));

      log.info('File uploaded to local helia successfully:', file.name);
    } catch (error) {
      log.error('Error uploading file:', error);
      throw error;
    }
  }, [dispatch, sixId, orgMeta, currentPath]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    void (async() => {
      try {
        // Process each selected file
        for (const file of Array.from(files)) {
          await uploadFile(file);
        }
      } catch (error) {
        log.error('Upload failed:', error);
        alert('Upload failed. Please try again.');
      } finally {
        // Reset the input so the same file can be selected again if needed
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    })();
  }, [uploadFile]);

  const handleUploadFile = useCallback(() => {
    // Trigger the file input dialog
    fileInputRef.current?.click();
  }, []);

  /**
   * Creates a folder with the given name in the current path.
   * Called by the CreateFolderModal after user enters name.
   */
  const createNewFolder = useCallback(async(folderName: string) => {
    // Sanitize the folder name (remove slashes)
    const sanitizedName = folderName.trim().replace(/\//g, '-');
    // Build the full path: currentPath + folderName + trailing slash
    const folderPath = normalizeFolderPath(currentPath + sanitizedName);

    log.debug('Creating new folder:', { name: sanitizedName, path: folderPath });

    await dispatch(createFolder({ sixId, folderName: sanitizedName, folderPath }))
      .unwrap()
      .then(() => {
        log.info('Folder created successfully:', folderPath);
      })
      .catch((error: unknown) => {
        log.error('Failed to create folder:', error);
        throw error;
      });
  }, [dispatch, sixId, currentPath]);

  return {
    isUploading,

    uploadProgress,
    currentPath,
    fileInputRef,
    handleUploadFile,
    handleFileSelect,
    createNewFolder,
  };
}
